import { supabase } from '@/integrations/supabase/client';

export interface BotSearchResult {
  id: string;
  name: string;
  signal_token: string;
  reverse_signal_token?: string;
  type: string;
  owner_id: string;
  status: string;
  created_date?: string;
}

/**
 * Search bots by name (case insensitive)
 */
export const searchBotsByName = async (name: string): Promise<BotSearchResult[]> => {
  if (!name.trim()) return [];

  const { data, error } = await supabase
    .from('bots')
    .select('id, name, signal_token, reverse_signal_token, type, owner_id, status, created_date')
    .ilike('name', `%${name.trim()}%`)
    .order('created_date', { ascending: false })
    .limit(20);

  if (error) {
    console.error('Error searching bots by name:', error);
    throw error;
  }

  return data || [];
};

/**
 * Search bots by signal token (signal_token or reverse_signal_token)
 */
export const searchBotsByToken = async (token: string): Promise<BotSearchResult[]> => {
  if (!token.trim()) return [];

  const { data, error } = await supabase
    .from('bots')
    .select('id, name, signal_token, reverse_signal_token, type, owner_id, status, created_date')
    .or(`signal_token.eq.${token.trim()},reverse_signal_token.eq.${token.trim()}`)
    .order('created_date', { ascending: false })
    .limit(20);

  if (error) {
    console.error('Error searching bots by token:', error);
    throw error;
  }

  return data || [];
};

/**
 * Get all bots with pagination (for admin overview)
 */
export const getAllBots = async (page: number = 0, limit: number = 50): Promise<{
  bots: BotSearchResult[];
  total: number;
}> => {
  // Get total count
  const { count } = await supabase
    .from('bots')
    .select('*', { count: 'exact', head: true });

  // Get paginated data
  const { data, error } = await supabase
    .from('bots')
    .select('id, name, signal_token, reverse_signal_token, type, owner_id, status, created_date')
    .order('created_date', { ascending: false })
    .range(page * limit, (page + 1) * limit - 1);

  if (error) {
    console.error('Error getting all bots:', error);
    throw error;
  }

  return {
    bots: data || [],
    total: count || 0
  };
};